import { writable } from 'svelte/store';

/**
 * Tracks the most recent project a task was moved to, client-side only.
 *
 * Used to surface a contextual "Move to <project>" shortcut in task menus so
 * users can keep moving tasks to the same project without re-searching. The
 * shortcut only shows for a short window (RECENT_MOVE_TTL_MS) after a move.
 *
 * Expiry is enforced by the store itself (a single module-level timer set on
 * each record, plus a freshness check when loading from localStorage), so
 * consumers just read the store reactively — no per-component countdown timers.
 */

export interface RecentMove {
  projectId: string;
  projectTitle: string;
  movedAt: number;
}

/** How long the "Move to <project>" shortcut stays available after a move. */
export const RECENT_MOVE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const STORAGE_KEY = 'recentMove';

function isRecentMove(value: unknown): value is RecentMove {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.projectId === 'string' &&
    typeof v.projectTitle === 'string' &&
    typeof v.movedAt === 'number'
  );
}

function load(): RecentMove | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecentMove(parsed)) return null;
    // Don't resurrect an expired record after a reload.
    if (Date.now() - parsed.movedAt > RECENT_MOVE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function save(value: RecentMove | null) {
  if (typeof localStorage === 'undefined') return;
  if (value) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

export const recentMove = writable<RecentMove | null>(load());
recentMove.subscribe(save);

let expiryTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleExpiry() {
  if (expiryTimer) clearTimeout(expiryTimer);
  expiryTimer = setTimeout(() => {
    expiryTimer = null;
    recentMove.set(null);
  }, RECENT_MOVE_TTL_MS);
}

/** Record that a task was moved to the given project. */
export function recordRecentMove(projectId: string, projectTitle: string) {
  recentMove.set({ projectId, projectTitle, movedAt: Date.now() });
  scheduleExpiry();
}

export function clearRecentMove() {
  if (expiryTimer) {
    clearTimeout(expiryTimer);
    expiryTimer = null;
  }
  recentMove.set(null);
}
